import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const LINKS = [
  { label: "How It Works", id: "how-it-works", testid: "nav-link-how-it-works" },
  { label: "Services", id: "services", testid: "nav-link-services" },
  { label: "Pricing", id: "pricing", testid: "nav-link-pricing" },
  { label: "FAQ", id: "faq", testid: "nav-link-faq" },
];

export const Wordmark = () => (
  <span className="inline-flex items-center gap-[7px]">
    <span className="font-display text-[22px] font-semibold leading-none tracking-[0.18em] text-ink">
      Leadtochair
    </span>
    <span
      className="h-[9px] w-[9px] rotate-45 rounded-[1.5px] bg-teal"
      aria-hidden="true"
    />
  </span>
);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 120 : 0);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-b border-ink/10 bg-paper/95 shadow-[0_1px_0_rgba(31,28,24,0.04)] backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <button
            data-testid="nav-wordmark"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="cursor-pointer"
            aria-label="Leadtochair — back to top"
          >
            <Wordmark />
          </button>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className="text-sm font-medium tracking-wide text-ink/70 transition-colors hover:text-teal"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-book-demo-btn"
              onClick={() => go("contact")}
              className="rounded-full bg-teal px-6 py-2.5 text-sm font-medium tracking-wide text-white transition-colors hover:bg-teal-dark"
            >
              Book a Demo
            </button>
          </nav>

          <button
            data-testid="nav-hamburger-btn"
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink md:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {open && (
        <div
          data-testid="mobile-menu-overlay"
          className="fixed inset-0 z-50 flex flex-col bg-paper px-6 py-5"
        >
          <div className="flex items-center justify-between">
            <Wordmark />
            <button
              data-testid="mobile-menu-close-btn"
              className="flex h-11 w-11 items-center justify-center rounded-full text-ink"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              <X size={26} />
            </button>
          </div>
          <nav className="mt-14 flex flex-col gap-2" aria-label="Mobile">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-${l.testid}`}
                onClick={() => go(l.id)}
                className="border-b border-ink/10 py-5 text-left font-display text-2xl text-ink transition-colors hover:text-teal"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-book-demo-btn"
              onClick={() => go("contact")}
              className="mt-10 rounded-full bg-teal px-8 py-4 text-base font-medium tracking-wide text-white transition-colors hover:bg-teal-dark"
            >
              Book a Demo
            </button>
          </nav>
        </div>
      )}
    </>
  );
}
